/* Chaos mode, ported from the v1 site's chaos.js.
 *
 * Same engine: a skull cursor with a difference-blend trail, every element
 * near the pointer shoved and hue-rotated, and a set of images from across
 * the work floating behind the page with gravity, collisions, drag and flick,
 * and Web Audio bumps when the one you last touched hits something.
 *
 * What changed in the port: the image list is built from the projects rather
 * than hard-coded v1 paths, and the header controls are exempt from the
 * distortion so the switch that turns it off stays clickable.
 */

import PROJECTS, { projectImageUrl } from './projectsData';

/* Every image the site has, as the engine's pool. */
function siteImages() {
  const out = [];
  PROJECTS.forEach((p) => {
    if (p.hero) out.push(projectImageUrl(p, p.hero));
    (p.media || []).forEach((m) => {
      if (m.type === 'img') out.push(projectImageUrl(p, m.src));
    });
  });
  return out;
}

/* Carried over from v1: these two are always in the mix, never left to the
   shuffle. They live outside the projects, in the site's chaos folder. */
const PINNED_IMAGES = [
  'chaos/catch.png',
  'chaos/hireme.png',
];

const C = {
  raf: null, trail: [], history: [], glow: null, cursorEl: null, mx: 0, my: 0,
  TRAIL_N: 20, HIST_N: 8, RADIUS: 250, FORCE: 30,
  floaters: [], dragging: null, dragOffX: 0, dragOffY: 0,
  gravity: false, GRAVITY_ACCEL: 0.18, FLOOR_BOUNCE: 0.6,
  audioCtx: null, lastBumpTime: 0, lastWallTime: 0, lastTouched: null,
  releaseTime: 0, SOUND_WINDOW: 2000,
  cachedEls: null, elFrame: 0, EL_REFRESH: 60,
  SEL: 'h1,h2,h3,p,li,span:not(.chaos-trail-dot):not(.chaos-floater),tr,th,td,a,button,.btn,label,summary,img:not(.chaos-floater),video,figure,details,hr,nav,pre,code,div.grid,strong,em,table',
};

/* --- audio --------------------------------------------------------------- */

function ensureAudio() {
  if (!C.audioCtx) {
    try {
      C.audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    } catch (e) { /* no audio support */ }
  }
  if (C.audioCtx && C.audioCtx.state === 'suspended') C.audioCtx.resume();
}

function soundAllowed() {
  if (C.dragging) return 1;
  if (!C.releaseTime) return 0;
  const elapsed = Date.now() - C.releaseTime;
  if (elapsed > C.SOUND_WINDOW) return 0;
  return 1 - elapsed / C.SOUND_WINDOW;
}

function playCollisionSound(force) {
  const fade = soundAllowed();
  if (!fade || !C.audioCtx || C.audioCtx.state !== 'running') return;
  const now = C.audioCtx.currentTime;
  if (now - C.lastBumpTime < 0.045) return;
  C.lastBumpTime = now;

  const vol = Math.min(force * 0.08, 0.4) * fade;
  if (vol < 0.005) return;
  const baseFreq = Math.max(120, 800 - force * 60);

  const osc = C.audioCtx.createOscillator();
  const gain = C.audioCtx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(baseFreq, now);
  osc.frequency.exponentialRampToValueAtTime(baseFreq * 0.3, now + 0.15);
  gain.gain.setValueAtTime(vol, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.18);
  osc.connect(gain); gain.connect(C.audioCtx.destination);
  osc.start(now); osc.stop(now + 0.2);

  const osc2 = C.audioCtx.createOscillator();
  const gain2 = C.audioCtx.createGain();
  osc2.type = 'triangle';
  osc2.frequency.setValueAtTime(baseFreq * 1.5, now);
  osc2.frequency.exponentialRampToValueAtTime(baseFreq * 0.5, now + 0.12);
  gain2.gain.setValueAtTime(vol * 0.4, now);
  gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.14);
  osc2.connect(gain2); gain2.connect(C.audioCtx.destination);
  osc2.start(now); osc2.stop(now + 0.16);
}

function playWallSound(speed) {
  const fade = soundAllowed();
  if (!fade || !C.audioCtx || C.audioCtx.state !== 'running') return;
  const now = C.audioCtx.currentTime;
  if (now - C.lastWallTime < 0.03) return;
  C.lastWallTime = now;

  const vol = Math.min(Math.abs(speed) * 0.03, 0.2) * fade;
  if (vol < 0.005) return;

  const osc = C.audioCtx.createOscillator();
  const gain = C.audioCtx.createGain();
  osc.type = 'square';
  osc.frequency.setValueAtTime(150, now);
  osc.frequency.exponentialRampToValueAtTime(60, now + 0.08);
  gain.gain.setValueAtTime(vol, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
  osc.connect(gain); gain.connect(C.audioCtx.destination);
  osc.start(now); osc.stop(now + 0.12);
}

/* --- floaters ------------------------------------------------------------ */

function spawnFloaters() {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let shuffled = siteImages();
  for (let i = shuffled.length - 1; i > 0; i--) {
    const r = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[r]] = [shuffled[r], shuffled[i]];
  }
  // drop any duplicates of the pinned pair, then put them at the front so
  // they always make the cut
  shuffled = PINNED_IMAGES.concat(
    shuffled.filter((src) => PINNED_IMAGES.indexOf(src) === -1)
  );
  const count = Math.min(shuffled.length, 25);

  for (let n = 0; n < count; n++) {
    const img = document.createElement('img');
    img.className = 'chaos-floater';
    img.src = shuffled[n];
    img.draggable = false;
    img.onerror = function () {
      const el = this;
      for (let k = C.floaters.length - 1; k >= 0; k--) {
        if (C.floaters[k].el === el) { C.floaters.splice(k, 1); break; }
      }
      this.remove();
    };
    document.body.appendChild(img);

    const w = 80 + Math.random() * 140;
    C.floaters.push({
      el: img, w,
      x: Math.random() * (vw - w),
      y: Math.random() * (vh - w * 0.7),
      vx: (Math.random() - 0.5) * 3,
      vy: (Math.random() - 0.5) * 3,
      rot: Math.random() * 360,
      vr: (Math.random() - 0.5) * 0.5,
      sx: 1, sy: 1,
      hueOff: Math.random() * 360,
      opacity: 1,
      mass: w * w * 0.001,
    });
  }
}

function destroyFloaters() {
  C.floaters.forEach((f) => f.el.remove());
  C.floaters = [];
  C.dragging = null;
}

function resolveCollisions() {
  for (let i = 0; i < C.floaters.length; i++) {
    const a = C.floaters[i];
    const ar = a.w * 0.35;
    const ax = a.x + a.w / 2;
    const ay = a.y + a.w * 0.35;

    for (let j = i + 1; j < C.floaters.length; j++) {
      const b = C.floaters[j];
      const br = b.w * 0.35;
      const bx = b.x + b.w / 2;
      const by = b.y + b.w * 0.35;

      const dx = bx - ax;
      const dy = by - ay;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const minDist = ar + br;

      if (dist < minDist && dist > 0) {
        const overlap = minDist - dist;
        const nx = dx / dist;
        const ny = dy / dist;

        const totalMass = a.mass + b.mass;
        const aRatio = b.mass / totalMass;
        const bRatio = a.mass / totalMass;

        if (a !== C.dragging) { a.x -= nx * overlap * aRatio; a.y -= ny * overlap * aRatio; }
        if (b !== C.dragging) { b.x += nx * overlap * bRatio; b.y += ny * overlap * bRatio; }

        const dvDotN = (a.vx - b.vx) * nx + (a.vy - b.vy) * ny;

        if (dvDotN > 0) {
          const impulse = dvDotN * 0.7;
          if (a !== C.dragging) { a.vx -= impulse * aRatio * nx; a.vy -= impulse * aRatio * ny; }
          if (b !== C.dragging) { b.vx += impulse * bRatio * nx; b.vy += impulse * bRatio * ny; }

          a.vr += (Math.random() - 0.5) * dvDotN * 0.08;
          b.vr += (Math.random() - 0.5) * dvDotN * 0.08;

          const squash = Math.min(dvDotN * 0.04, 0.3);
          a.sx = 1 + squash; a.sy = 1 - squash * 0.5;
          b.sx = 1 + squash; b.sy = 1 - squash * 0.5;

          if (a === C.lastTouched || b === C.lastTouched) playCollisionSound(dvDotN);
        }
      }
    }
  }
}

/* --- dragging ------------------------------------------------------------ */

/* The floaters sit at z-index -1, behind the page, so on a dense grid the
   containers above them swallow every click and e.target is never the image.
   v1 got away with checking e.target because its pages were mostly bare
   background. Here the hit test is done against the floaters' own boxes
   instead, topmost first, so they can be grabbed wherever they are. */
function floaterAt(x, y) {
  for (let i = C.floaters.length - 1; i >= 0; i--) {
    const b = C.floaters[i].el.getBoundingClientRect();
    if (x >= b.left && x <= b.right && y >= b.top && y <= b.bottom) return C.floaters[i];
  }
  return null;
}

function floaterDown(e) {
  if (!document.body.classList.contains('chaos')) return;
  const clientX = e.touches ? e.touches[0].clientX : e.clientX;
  const clientY = e.touches ? e.touches[0].clientY : e.clientY;

  const hit = floaterAt(clientX, clientY);
  if (!hit) return;   // nothing under the pointer, let the page have the click

  e.preventDefault();
  C.dragging = hit;
  C.lastTouched = hit;
  C.dragOffX = clientX - hit.x;
  C.dragOffY = clientY - hit.y;
  hit.vx = 0;
  hit.vy = 0;
  hit._lastX = clientX;
  hit._lastY = clientY;
}

function floaterMove(e) {
  if (!C.dragging) return;
  e.preventDefault();
  const clientX = e.touches ? e.touches[0].clientX : e.clientX;
  const clientY = e.touches ? e.touches[0].clientY : e.clientY;
  C.dragging.x = clientX - C.dragOffX;
  C.dragging.y = clientY - C.dragOffY;
  C.dragging.vx = (clientX - C.dragging._lastX) * 0.5;
  C.dragging.vy = (clientY - C.dragging._lastY) * 0.5;
  C.dragging._lastX = clientX;
  C.dragging._lastY = clientY;

  const speed = Math.sqrt(C.dragging.vx ** 2 + C.dragging.vy ** 2);
  const angle = Math.atan2(C.dragging.vy, C.dragging.vx);
  const stretch = Math.min(speed * 0.05, 0.6);
  C.dragging.sx = 1 + Math.abs(Math.cos(angle)) * stretch;
  C.dragging.sy = 1 + Math.abs(Math.sin(angle)) * stretch;
}

function floaterUp() {
  if (C.dragging) {
    C.releaseTime = Date.now();
    C.dragging = null;
  }
}

/* --- frame --------------------------------------------------------------- */

function chaosMove(e) { C.mx = e.clientX; C.my = e.clientY; }



function chaosFrame() {
  if (!document.body.classList.contains('chaos')) return;

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const hue = (C.mx / vw) * 360;
  document.body.style.setProperty('--chaos-hue', hue);

  for (let fi = 0; fi < C.floaters.length; fi++) {
    const f = C.floaters[fi];
    if (f !== C.dragging) {
      f.x += f.vx;
      f.y += f.vy;
      f.rot += f.vr;

      if (C.gravity) f.vy += C.GRAVITY_ACCEL;

      f.vx *= C.gravity ? 0.998 : 0.997;
      f.vy *= C.gravity ? 0.999 : 0.997;
      f.vr *= 0.98;

      const fh = f.w * 0.7;
      const isActive = f === C.lastTouched;
      if (f.x < 0) {
        f.x = 0; const o = f.vx; f.vx = Math.abs(f.vx) * 0.8; f.vr += (Math.random() - 0.5) * 0.5;
        if (isActive) playWallSound(o);
      }
      if (f.x + f.w > vw) {
        f.x = vw - f.w; const o = f.vx; f.vx = -Math.abs(f.vx) * 0.8; f.vr += (Math.random() - 0.5) * 0.5;
        if (isActive) playWallSound(o);
      }
      if (f.y < 0) {
        f.y = 0; const o = f.vy; f.vy = Math.abs(f.vy) * 0.8; f.vr += (Math.random() - 0.5) * 0.5;
        if (isActive) playWallSound(o);
      }
      if (f.y + fh > vh) {
        f.y = vh - fh; const o = f.vy;
        f.vy = -Math.abs(f.vy) * C.FLOOR_BOUNCE;
        f.vr += (Math.random() - 0.5) * 0.5;
        if (C.gravity) f.vx *= 0.95;
        if (isActive) playWallSound(o);
      }

      const fcx = f.x + f.w / 2;
      const fcy = f.y + f.w * 0.35;
      const fdx = C.mx - fcx;
      const fdy = C.my - fcy;
      const fdist = Math.sqrt(fdx * fdx + fdy * fdy);
      if (fdist < 200 && fdist > 0) {
        const force = (1 - fdist / 200) * 1.5;
        f.vx -= (fdx / fdist) * force;
        f.vy -= (fdy / fdist) * force;
        f.vr += (fdx > 0 ? 1 : -1) * force * 0.08;
      }

      f.sx += (1 - f.sx) * 0.15;
      f.sy += (1 - f.sy) * 0.15;
    }

    const fHue = (hue + f.hueOff) % 360;
    f.el.style.cssText =
      'position:fixed;z-index:-1;margin:0!important;' +
      `left:${f.x.toFixed(0)}px;top:${f.y.toFixed(0)}px;` +
      `width:${f.w.toFixed(0)}px;height:auto;` +
      `opacity:${f.opacity.toFixed(2)};` +
      `filter:hue-rotate(${fHue.toFixed(0)}deg) saturate(1.4) brightness(0.9);` +
      `transform:rotate(${f.rot.toFixed(1)}deg) scaleX(${f.sx.toFixed(3)}) scaleY(${f.sy.toFixed(3)});` +
      'pointer-events:auto;user-select:none;-webkit-user-drag:none;will-change:transform,filter;';
  }

  resolveCollisions();

  C.history.unshift({ x: C.mx, y: C.my });
  if (C.history.length > C.HIST_N) C.history.pop();

  if (C.cursorEl) {
    C.cursorEl.style.left = `${C.mx}px`;
    C.cursorEl.style.top = `${C.my}px`;
  }

  for (let i = 0; i < C.trail.length; i++) {
    const t = C.trail[i];
    const target = i === 0 ? { x: C.mx, y: C.my } : C.trail[i - 1];
    const ease = 0.45 - i * 0.015;
    t.x += (target.x - t.x) * ease;
    t.y += (target.y - t.y) * ease;
    const dotHue = (hue + i * 18) % 360;
    const scale = 1 - (i / C.trail.length) * 0.6;
    const opacity = 1 - i / C.trail.length;
    t.el.style.cssText =
      'position:fixed;pointer-events:none;z-index:9999;mix-blend-mode:difference;margin:0!important;' +
      `left:${t.x}px;top:${t.y}px;` +
      'width:2ch;height:calc(var(--line-height,1.2rem)*1.5);' +
      `background:hsl(${dotHue},100%,55%);` +
      `opacity:${opacity.toFixed(2)};` +
      `transform:translate(-50%,-50%) scale(${scale.toFixed(2)}) rotate(${i * 8}deg);`;
  }

  C.elFrame++;
  if (!C.cachedEls || C.elFrame >= C.EL_REFRESH) {
    C.cachedEls = document.body.querySelectorAll(C.SEL);
    C.elFrame = 0;
  }
  const els = C.cachedEls;
  for (let j = 0; j < els.length; j++) {
    const el = els[j];
    if (el.closest('.chaos-trail-dot') || el.closest('.chaos-glow')
        || el.classList.contains('chaos-floater')
        // leave the header controls alone, or the switch that turns this off
        // slides out from under the pointer
        || el.closest('.header-controls')) continue;

    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = C.mx - cx;
    const dy = C.my - cy;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist < C.RADIUS) {
      const intensity = 1 - dist / C.RADIUS;
      const i2 = intensity * intensity;
      const angle = Math.atan2(dy, dx);
      const pushX = -Math.cos(angle) * C.FORCE * i2;
      const pushY = -Math.sin(angle) * C.FORCE * i2;
      const skew = intensity * 4;
      const rotDeg = intensity * 3 * (dx > 0 ? 1 : -1);
      const scl = 1 + intensity * 0.08;

      const tag = el.tagName;
      const isClickable = tag === 'A' || tag === 'BUTTON' || el.classList.contains('btn')
        || el.closest('a') || el.closest('button');
      // anything holding a link stays put as well, or the link rides away on
      // its parent's push and there is nothing left to aim at
      const holdsClickable = !isClickable && el.querySelector
        && !!el.querySelector('a,button,.btn');
      const px = (isClickable || holdsClickable) ? 0 : pushX;
      const py = (isClickable || holdsClickable) ? 0 : pushY;

      if (tag === 'IMG' || tag === 'VIDEO' || tag === 'FIGURE') {
        el.style.filter = `hue-rotate(${(hue + intensity * 120).toFixed(0)}deg) saturate(${(1 + intensity * 3).toFixed(1)}) brightness(${(1 + intensity * 0.3).toFixed(2)})`;
        el.style.transform = `translate(${pushX.toFixed(1)}px,${pushY.toFixed(1)}px) scale(${scl.toFixed(3)})`;
      } else if (isClickable) {
        // still lit up like everything else, just calm enough to hit: a
        // third of the old skew and rotation, and barely any growth
        el.style.transform = `skew(${(intensity * 4).toFixed(1)}deg) rotate(${(intensity * 2.5 * (dx > 0 ? 1 : -1)).toFixed(1)}deg) scale(${(1 + intensity * 0.05).toFixed(3)})`;
        // colour only. Brightness and contrast were washing the text out to
        // white once a link stopped running away and sat at full intensity
        el.style.filter = `hue-rotate(${(hue + intensity * 180).toFixed(0)}deg) saturate(${(1 + intensity * 3).toFixed(1)})`;
      } else if (holdsClickable) {
        el.style.transform = `skew(${(skew * 0.4).toFixed(1)}deg) rotate(${(rotDeg * 0.4).toFixed(1)}deg) scale(${(1 + intensity * 0.02).toFixed(3)})`;
        // no brightness here either: it compounds onto the link inside
        el.style.filter = `hue-rotate(${(hue + intensity * 90).toFixed(0)}deg)`;
      } else {
        el.style.transform = `translate(${px.toFixed(1)}px,${py.toFixed(1)}px) skew(${skew.toFixed(1)}deg) rotate(${rotDeg.toFixed(1)}deg) scale(${scl.toFixed(3)})`;
        el.style.filter = `hue-rotate(${(hue + intensity * 90).toFixed(0)}deg) brightness(${(1 + intensity * 0.5).toFixed(2)})`;
      }
      el.setAttribute('data-cd', '1');
    } else if (el.hasAttribute('data-cd')) {
      el.style.transform = '';
      el.style.filter = '';
      el.removeAttribute('data-cd');
    }
  }

  C.raf = requestAnimationFrame(chaosFrame);
}

/* --- public -------------------------------------------------------------- */

export function startChaos() {
  if (C.raf) return;
  document.body.classList.add('chaos');

  for (let i = 0; i < C.TRAIL_N; i++) {
    const d = document.createElement('span');
    d.className = 'chaos-trail-dot';
    document.body.appendChild(d);
    C.trail.push({ el: d, x: 0, y: 0 });
  }
  for (let j = 0; j < C.HIST_N; j++) C.history.push({ x: 0, y: 0 });

  document.documentElement.classList.add('chaos-active');

  C.glow = document.createElement('div');
  C.glow.className = 'chaos-glow';
  document.body.appendChild(C.glow);

  C.cursorEl = document.createElement('div');
  C.cursorEl.className = 'chaos-cursor';
  C.cursorEl.textContent = '💀';
  document.body.appendChild(C.cursorEl);

  spawnFloaters();
  ensureAudio();

  document.addEventListener('mousemove', chaosMove);
  document.addEventListener('mousedown', floaterDown);
  document.addEventListener('mousemove', floaterMove);
  document.addEventListener('mouseup', floaterUp);
  document.addEventListener('touchstart', floaterDown, { passive: false });
  document.addEventListener('touchmove', floaterMove, { passive: false });
  document.addEventListener('touchend', floaterUp);

  C.raf = requestAnimationFrame(chaosFrame);
}

export function stopChaos() {
  document.body.classList.remove('chaos');
  document.removeEventListener('mousemove', chaosMove);
  document.removeEventListener('mousedown', floaterDown);
  document.removeEventListener('mousemove', floaterMove);
  document.removeEventListener('mouseup', floaterUp);
  document.removeEventListener('touchstart', floaterDown);
  document.removeEventListener('touchmove', floaterMove);
  document.removeEventListener('touchend', floaterUp);

  if (C.raf) cancelAnimationFrame(C.raf);
  C.raf = null;
  C.trail.forEach((t) => t.el.remove());
  C.trail = [];
  C.history = [];
  document.documentElement.classList.remove('chaos-active');
  if (C.glow) { C.glow.remove(); C.glow = null; }
  if (C.cursorEl) { C.cursorEl.remove(); C.cursorEl = null; }
  C.cachedEls = null;
  C.elFrame = 0;
  destroyFloaters();
  document.querySelectorAll('[data-cd]').forEach((el) => {
    el.style.transform = '';
    el.style.filter = '';
    el.removeAttribute('data-cd');
  });
}

export function isChaosOn() {
  return !!C.raf;
}

/* The route changes under it, so re-cache on demand. */
export function refreshChaosTargets() {
  C.cachedEls = null;
  C.elFrame = 0;
}
